import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import moment from "moment";
import { getNotification, readNotification, readAllNotifications } from "../../../src/redux/notification/notificationAction";
import profileImage from "../../assests/defaultImage.png";
import defaultEventImage from "../../assests/default_event.png";
import defaultGroupImage from "../../assests/default_group.png";
import { FiBellOff } from "react-icons/fi";
import { FaCheck } from "react-icons/fa6";

const categorizeNotifications = (notifications) => {
  const newNotifs = [];
  const thisWeekNotifs = [];
  const thisMonthNotifs = [];
  const othersNotifs = [];

  notifications.forEach(notification => {
    const daysDifference = moment().diff(moment(notification.createdAt), 'days');

    if (daysDifference <= 3) {
      newNotifs.push(notification);
    } else if (daysDifference <= 7) {
      thisWeekNotifs.push(notification);
    } else if (daysDifference <= 30) {
      thisMonthNotifs.push(notification);
    } else {
      othersNotifs.push(notification);
    }
  });

  return [
    { title: 'New', data: newNotifs.sort((a, b) => moment(b.createdAt) - moment(a.createdAt)) },
    { title: 'This Week', data: thisWeekNotifs.sort((a, b) => moment(b.createdAt) - moment(a.createdAt)) },
    { title: 'This Month', data: thisMonthNotifs.sort((a, b) => moment(b.createdAt) - moment(a.createdAt)) },
    { title: 'Older', data: othersNotifs.sort((a, b) => moment(b.createdAt) - moment(a.createdAt)) },
  ].filter(section => section.data.length > 0);
};

const NotificationItem = React.memo(({ notification, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { read, payload, createdAt, id, type: ItemType } = notification ?? {};
  const { body, data, title } = payload ?? {};
  const { user, image, postId, groupId, eventId: directEventId, type: DataType, event } = data ?? {};
  const { profilePhoto, userId } = user ?? {};
  const type = DataType || ItemType;
  const eventId = directEventId || event?.eventId || event?._id || event?.id;
  const userAvatar = profilePhoto?.length > 0 ? profilePhoto[0]?.photoPublicUrl : profileImage;
  const defaultImage = type?.includes("group") ? defaultGroupImage : type?.includes("event") ? defaultEventImage : userAvatar;

  // Build the event-reminder message from the actual startTime instead of the
  // backend's static `body`, so it stays accurate (and switches to past tense)
  // regardless of how long ago the notification was created.
  const displayBody = useMemo(() => {
    const eventStartRaw = data?.startTime || event?.startdateandtime || data?.startdateandtime || event?.startDate || data?.startDate;
    let result = body || "";

    if ((type === 'eventReminder' || /remind/i.test(title || body || type)) && eventStartRaw) {
      const eventTime = moment.utc(eventStartRaw).local();
      const now = moment();
      const isPast = eventTime.isBefore(now);
      const timeStr = eventTime.format('h:mm A');
      const dayStr = eventTime.isSame(now, 'day') ? 'today' : `on ${eventTime.format('MMMM Do YYYY')}`;
      const eventNameDisplay = event?.eventName || data?.eventName || title || '';
      result = `${eventNameDisplay} ${isPast ? 'took place' : 'is happening'} ${dayStr} at ${timeStr}.`;
    }
    return result;
  }, [event, data, title, body, type]);

  const handleNavigation = useCallback(() => {
    if (!read) {
      dispatch(readNotification(id));
    }
    onClose?.();

    const navMap = {
      AcceptedRequest: () => navigate(`/user/${userId}`),
      FriendRequest: () => navigate('/friends'),
      CommentOnPost: () => navigate(`/post/${postId}`),
      CommentOnReel: () => navigate(`/flips/${postId}?cm=true`),
      groupAdded: () => navigate(`/group/${groupId}`),
      groupPost: () => navigate(`/group/${groupId}`),
    };

    if (navMap[type]) {
      navMap[type]();
    } else if ((type === 'eventInvitation' || type === 'eventReminder' || type?.toLowerCase()?.includes('event')) && eventId) {
      navigate(`/events/${eventId}`);
    }
  }, [read, dispatch, id, onClose, navigate, userId, postId, groupId, eventId, type]);

  const handleMarkAsRead = useCallback((e) => {
    e.stopPropagation();
    dispatch(readNotification(id));
  }, [dispatch, id]);

  return (
    <motion.div
      layout="position"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className={`flex gap-3 p-3 rounded-md cursor-pointer ${!read ? "bg-customGray" : ""} hover:bg-customGray`}
      onClick={handleNavigation}
    >
      <img
        src={image || defaultImage}
        alt="notification"
        className="w-12 h-12 rounded-full border bg-white object-cover flex-shrink-0"
        loading="lazy"
      />
      <div className="flex-1 text-left min-w-0">
        <p className="text-sm break-words">
          <span className="font-semibold">{title}</span> {displayBody}
        </p>
        <p className="text-gray-400 text-xs mt-1">{moment(createdAt).fromNow()}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {!read && (
          <button
            onClick={handleMarkAsRead}
            className="text-xs text-customGold whitespace-nowrap hover:underline"
            aria-label="Mark as read"
          >
            Mark as Read
          </button>
        )}
      </div>
    </motion.div>
  );
});

NotificationItem.displayName = 'NotificationItem';

const CategoryNotification = React.memo(({ notificationsections, onClose }) => {
  return (
    <div className="text-sm text-white mb-4">
      <h1 className="font-medium text-base mb-2">{notificationsections?.title}</h1>
      <div className="w-full border-t border-t-gray-600 my-2" />
      <div className="flex flex-col gap-2">
        {notificationsections?.data?.map((notification) => (
          <NotificationItem
            key={notification?.id}
            notification={notification}
            onClose={onClose}
          />
        ))}
      </div>
    </div>
  );
});

CategoryNotification.displayName = 'CategoryNotification';

const Notification = ({ notifications, unReadNotifications, loading, onClose }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("All");
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const categorizedNotifications = useMemo(() =>
    categorizeNotifications(
      (activeTab === 'All' || unReadNotifications?.length === 0) ? notifications : unReadNotifications
    ),
    [activeTab, notifications, unReadNotifications]
  );

  useEffect(() => {
    dispatch(getNotification());
  }, [dispatch]);

  const handleMarkAllAsRead = useCallback(async () => {
    if (unReadNotifications.length === 0) return;

    setMarkingAllRead(true);
    try {
      await dispatch(readAllNotifications()).unwrap();
      await dispatch(getNotification());
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    } finally {
      setMarkingAllRead(false);
    }
  }, [dispatch, unReadNotifications.length]);

  const showEmptyState = !loading && categorizedNotifications.length === 0;
  const showLoadingState = loading && (!notifications || notifications.length === 0);

  return (
    <div className="bg-customDark shadow-black overflow-hidden absolute text-white p-4 mt-6 right-0 rounded-lg w-full sm:w-[500px] h-[500px] shadow-md animate-fade-in">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Notifications</h2>
        {unReadNotifications.length > 0 && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleMarkAllAsRead}
            disabled={markingAllRead}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-customGold text-black text-sm font-medium transition-all ${markingAllRead ? "opacity-50 cursor-not-allowed" : "hover:bg-opacity-90"}`}
          >
            {markingAllRead ? (
              <>
                <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" />
                <span>Marking...</span>
              </>
            ) : (
              <>
                <FaCheck size={16} />
                <span className="text-xs whitespace-nowrap text-black">Mark All</span>
              </>
            )}
          </motion.button>
        )}
      </div>

      {!loading && (
        <div className="flex gap-2 mb-4">
          <button
            className={`relative px-3 py-1 rounded-full transition ${activeTab === "All" ? "bg-customGold text-black" : "bg-customGray"}`}
            onClick={() => setActiveTab("All")}
          >
            All
          </button>
          {unReadNotifications.length > 0 && (
            <button
              className={`relative px-3 py-1 rounded-full transition ${activeTab === "Unread" ? "bg-customGold text-black" : "bg-customGray"}`}
              onClick={() => setActiveTab("Unread")}
            >
              Unread
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                {unReadNotifications.length}
              </span>
            </button>
          )}
        </div>
      )}

      {showEmptyState && (
        <div className="flex flex-col items-center justify-center h-[70%] text-center p-4">
          <FiBellOff className="w-24 h-24 opacity-50 mb-4 text-gray-400" />
          <h3 className="text-lg font-medium mb-2">No Notifications Yet</h3>
          <p className="text-gray-400 max-w-xs">
            {activeTab === "All" ? "You don't have any notifications yet." : "You've read all your notifications."}
          </p>
        </div>
      )}

      {showLoadingState && (
        <div className="flex flex-col items-center justify-center h-[70%]">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-24 h-24 rounded-full bg-customGray mb-4" />
            <div className="h-4 w-32 bg-customGray rounded mb-2" />
            <div className="h-3 w-48 bg-customGray rounded" />
          </div>
        </div>
      )}

      {!showEmptyState && !showLoadingState && (
        <div className="h-full flex-1 flex flex-col gap-2 overflow-y-scroll pb-20 scrollbar-hide">
          {categorizedNotifications.map((section) => (
            <CategoryNotification
              key={section.title}
              notificationsections={section}
              onClose={onClose}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(Notification);